/**
 * iter-95 — Browser playback unlock.
 *
 * Chrome/Safari/Firefox block `HTMLMediaElement.play()` unless the call
 * happens within a user-gesture (click/tap/keydown). Our app generates
 * songs in the background and tries to autoplay them when they finish —
 * which the browser silently rejects with a `NotAllowedError`, leaving
 * the user staring at a Play button that does nothing on first click.
 *
 * Fix: on the FIRST user interaction with the page, prime a global
 * AudioContext + a silent <audio> element. Browsers treat that as
 * permission for any subsequent .play() call in the same session.
 *
 * Also exports `safePlay()` — a wrapper around HTMLAudioElement.play()
 * that catches NotAllowedError and shows a friendly "Click to play"
 * toast prompting the user to tap.
 */
import { toast } from "sonner";

let _unlocked = false;
let _audioCtx = null;
let _silentAudio = null;
let _listenerInstalled = false;

function _doUnlock() {
  if (_unlocked) return;
  _unlocked = true;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      _audioCtx = new Ctx();
      if (_audioCtx.state === "suspended") _audioCtx.resume();
    }
  } catch (e) { void e; }
  try {
    if (!_silentAudio) {
      _silentAudio = new Audio(
        // 1 sample of silence — base64 WAV (44 bytes).
        "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA="
      );
      _silentAudio.volume = 0;
    }
    const p = _silentAudio.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch (e) { void e; }
}

/** Wire up the global click/touch/key listener once. Subsequent calls
 *  are cheap no-ops so it's safe to call from React effects. */
export function installPlaybackUnlock() {
  if (_listenerInstalled || typeof window === "undefined") return;
  _listenerInstalled = true;
  const onAnyGesture = () => {
    _doUnlock();
    // Detach as soon as we've armed the context — no need to keep firing.
    window.removeEventListener("pointerdown", onAnyGesture);
    window.removeEventListener("keydown", onAnyGesture);
  };
  window.addEventListener("pointerdown", onAnyGesture, { once: true, passive: true });
  window.addEventListener("keydown", onAnyGesture, { once: true });
}

/** Async-safe wrapper around HTMLAudioElement.play(). If the browser
 *  blocks the call (NotAllowedError), shows a one-line toast and resolves
 *  to false so the caller can degrade gracefully. Returns true on success.
 */
export async function safePlay(audioEl) {
  if (!audioEl) return false;
  try {
    await audioEl.play();
    return true;
  } catch (err) {
    const name = err?.name || "";
    if (name === "NotAllowedError" || name === "NotSupportedError") {
      toast.warning("Click anywhere to enable playback", {
        description: "Your browser blocked autoplay — tap the page once and the song will play.",
        action: {
          label: "Tap here",
          onClick: () => {
            _doUnlock();
            audioEl.play().catch(() => {});
          },
        },
        duration: 8000,
      });
      return false;
    }
    throw err;
  }
}

/** True once the user has interacted with the page (or we've otherwise
 *  primed the AudioContext). UIs can check this to decide whether to
 *  auto-play or show a "Tap to play" overlay. */
export function isPlaybackUnlocked() { return _unlocked; }
